import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Trash2, ShieldAlert } from "lucide-react";

const TARGETS = [
  {
    group: "Leads",
    items: [
      { key: "leads_rental", label: "Rental leads", desc: "Deletes rental leads + their contracts/payments/blocks/notes", countKey: "leads_rental" },
      { key: "leads_birthday_party", label: "Birthday Party leads", desc: "Deletes birthday party leads + their related data", countKey: "leads_birthday_party" },
      { key: "leads_event", label: "Event leads", desc: "Deletes event leads + their related data", countKey: "leads_event" },
      { key: "leads_picnic", label: "Picnic leads", desc: "Deletes picnic leads + their related data", countKey: "leads_picnic" },
      { key: "leads_all", label: "ALL leads", desc: "Deletes every lead and all their contracts/payments/blocks/notes", countKey: "leads", danger: true },
    ],
  },
  {
    group: "Contracts & Waivers",
    items: [
      { key: "contracts_pending", label: "Pending contracts only", desc: "Sent but not yet signed", countKey: "contracts_pending" },
      { key: "contracts_signed", label: "Signed contracts only", desc: "Already e-signed (PDFs are kept on disk)", countKey: "contracts_signed" },
      { key: "contracts_all", label: "All contracts/waivers/addendums", desc: "Wipes the contracts collection entirely", countKey: null, danger: true },
    ],
  },
  {
    group: "Finances",
    items: [
      { key: "payments", label: "Payment records", desc: "Clears all recorded payments", countKey: "payments" },
    ],
  },
  {
    group: "System",
    items: [
      { key: "calendar_blocks", label: "Manual calendar blocks", desc: "Keeps Google-synced events; clears manual/tentative/booked blocks", countKey: "calendar_blocks" },
      { key: "audit_log", label: "Audit log", desc: "Clears action history", countKey: "audit_log" },
      { key: "everything", label: "🔥 EVERYTHING (nuke)", desc: "Wipes leads, contracts, payments, blocks, notes, audit log, emails. Seeds (users, venues, templates, Google) preserved.", countKey: null, danger: true },
    ],
  },
];

export default function AdminToolsPage() {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  const loadCounts = () => {
    setLoading(true);
    api.get("/admin/testing/counts")
      .then(({ data }) => setCounts(data || {}))
      .catch(() => toast.error("Failed to load counts"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadCounts(); }, []);

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
  const selectedKeys = Object.keys(selected).filter((k) => selected[k]);

  const handleRun = async () => {
    if (passcode !== "1993") {
      toast.error("Invalid passcode");
      return;
    }
    if (selectedKeys.length === 0) {
      toast.error("Pick at least one target to clear");
      return;
    }
    setRunning(true);
    const summary = {};
    try {
      // Run sequentially so audit log entries are coherent
      for (const target of selectedKeys) {
        const { data } = await api.post("/admin/testing/wipe", { passcode: "1993", target });
        Object.entries(data.counts || {}).forEach(([k, v]) => {
          summary[k] = (summary[k] || 0) + v;
        });
      }
      setResults({ targets: selectedKeys, counts: summary });
      setSelected({});
      setPasscode("");
      setConfirmOpen(false);
      loadCounts();
      toast.success("Test data cleared");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear failed");
    } finally {
      setRunning(false);
    }
  };

  const totalSelected = selectedKeys.length;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-tools-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-outfit font-semibold flex items-center gap-2">
            <ShieldAlert size={24} className="text-amber-500" />
            Testing Tools
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Super-admin utilities to clear test data before launch. Protected by a separate passcode.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadCounts} data-testid="refresh-counts-btn">
          <RefreshCw size={14} className="mr-2" /> Refresh counts
        </Button>
      </div>

      <Card className="border-amber-500/40 bg-amber-500/5">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            These actions are <span className="font-semibold text-foreground">irreversible</span>. Use only while testing.
            Seeded data (users, venues, contract templates, Google connections) is always preserved.
          </div>
        </CardContent>
      </Card>

      {TARGETS.map((g) => (
        <Card key={g.group} data-testid={`group-${g.group.toLowerCase().replace(/\s+/g, "-")}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-outfit">{g.group}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {g.items.map((item) => {
              const count = item.countKey != null ? counts[item.countKey] : null;
              return (
                <label
                  key={item.key}
                  data-testid={`target-${item.key}`}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selected[item.key]
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-accent/40"
                  } ${item.danger ? "border-destructive/30" : ""}`}
                >
                  <Checkbox
                    checked={!!selected[item.key]}
                    onCheckedChange={() => toggle(item.key)}
                    data-testid={`checkbox-${item.key}`}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${item.danger ? "text-destructive" : "text-foreground"}`}>
                        {item.label}
                      </span>
                      {loading ? (
                        <Skeleton className="h-5 w-12" />
                      ) : count != null ? (
                        <Badge variant="secondary" data-testid={`count-${item.key}`}>{count} record{count === 1 ? "" : "s"}</Badge>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </label>
              );
            })}
          </CardContent>
        </Card>
      ))}

      <div className="sticky bottom-4 z-10">
        <Card className="shadow-lg border-primary/30">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm">
              <span className="font-medium">{totalSelected}</span>{" "}
              <span className="text-muted-foreground">target{totalSelected === 1 ? "" : "s"} selected</span>
            </div>
            <Button
              variant="destructive"
              disabled={totalSelected === 0}
              onClick={() => setConfirmOpen(true)}
              data-testid="open-confirm-btn"
            >
              <Trash2 size={14} className="mr-2" />
              Clear selected
            </Button>
          </CardContent>
        </Card>
      </div>

      {results && (
        <Card className="border-green-500/40 bg-green-500/5" data-testid="results-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Last clear summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="text-xs text-muted-foreground">
              Targets: {results.targets.join(", ")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {Object.entries(results.counts).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between p-2 rounded border border-border bg-card">
                  <span className="text-xs text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="text-sm font-semibold" data-testid={`result-${k}`}>{v}</span>
                </div>
              ))}
              {Object.keys(results.counts).length === 0 && (
                <span className="text-xs text-muted-foreground col-span-full">No records were affected.</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Passcode confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setPasscode(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert size={18} className="text-destructive" />
              Confirm test data wipe
            </DialogTitle>
            <DialogDescription>
              You're about to clear {totalSelected} target{totalSelected === 1 ? "" : "s"}. This cannot be undone. Enter the testing passcode to proceed.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded border border-border bg-muted/30 p-3 max-h-40 overflow-auto text-xs space-y-1">
            {selectedKeys.map((k) => (
              <div key={k} className="font-mono">• {k}</div>
            ))}
          </div>
          <Input
            data-testid="testing-passcode-input"
            type="password"
            placeholder="Testing passcode"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !running && handleRun()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={running}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleRun}
              disabled={running}
              data-testid="confirm-wipe-btn"
            >
              {running ? "Clearing..." : "Yes, clear data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
